import { useState, useEffect, createContext, useContext } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { FREE_TIER } from '../config/stripe';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Get additional user data from Firestore.
        // Wrap in try/catch — permission-denied can fire before the auth token propagates.
        let userData = {};
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          userData = userDoc.exists() ? userDoc.data() : {};
        } catch (err) {
          if (err?.code !== 'permission-denied') console.warn('[useAuth] getDoc error:', err);
        }
        
        // Determine subscription status
        const subStatus = userData.subscription || { tier: 'free', status: 'active' };
        setSubscription(subStatus);
        
        const isPaid = subStatus.tier === 'pro' || subStatus.tier === 'lifetime';
        setUser({
          ...userData,
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          userType: userData.userType || 'user',
          // Computed fields always override Firestore values
          isAdmin: userData.userType === 'admin',
          isPro: isPaid,
          isLifetime: subStatus.tier === 'lifetime' || Boolean(subStatus.isLifetime),
          sheetCount: userData.sheetCount || 0,
          shareCredits: userData.shareCredits ?? (subStatus.tier === 'lifetime' ? Infinity : 0),
          canUpload: (userData.sheetCount || 0) < FREE_TIER.maxSheets || isPaid,
          subscription: subStatus,
        });
      } else {
        setUser(null);
        setSubscription(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Real-time subscription listener
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const subStatus = data.subscription || { tier: 'free', status: 'active' };
          setSubscription(subStatus);
          
          const isPaid = subStatus.tier === 'pro' || subStatus.tier === 'lifetime';
          setUser(prev => ({
            ...prev,
            isPro: isPaid,
            isLifetime: subStatus.tier === 'lifetime' || subStatus.isLifetime,
            sheetCount: data.sheetCount || 0,
            shareCredits: data.shareCredits ?? (subStatus.tier === 'lifetime' ? Infinity : 0),
            canUpload: (data.sheetCount || 0) < FREE_TIER.maxSheets || isPaid,
            subscription: subStatus,
          }));
        }
      },
      (err) => {
        // Silently ignore permission errors during auth token propagation
        if (err.code !== 'permission-denied') {
          console.warn('User snapshot error:', err.message);
        }
      }
    );

    return unsubscribe;
  }, [user?.uid]);

  const signIn = async (email, password) => {
    setError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signUp = async (email, password, displayName) => {
    setError(null);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name
      await updateProfile(result.user, { displayName });
      
      // Create user document in Firestore with subscription info
      await setDoc(doc(db, 'users', result.user.uid), {
        email,
        displayName,
        createdAt: new Date().toISOString(),
        userType: 'user',
        sheetCount: 0,
        subscription: {
          tier: 'free',
          status: 'active',
          startedAt: new Date().toISOString(),
        },
        settings: {
          defaultPageTurnMode: 'timer',
          defaultBPM: 120,
          theme: 'light'
        }
      });
      
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signInWithGoogle = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if user doc exists, if not create it
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', result.user.uid), {
          email: result.user.email,
          displayName: result.user.displayName,
          createdAt: new Date().toISOString(),
          userType: 'user',
          sheetCount: 0,
          subscription: {
            tier: 'free',
            status: 'active',
            startedAt: new Date().toISOString(),
          },
          settings: {
            defaultPageTurnMode: 'timer',
            defaultBPM: 120,
            theme: 'light'
          }
        });
      }
      
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Helper to check if user can upload more sheets
  const checkCanUpload = () => {
    if (!user) return false;
    if (user.isPro) return true;
    return (user.sheetCount || 0) < FREE_TIER.maxSheets;
  };

  // Update user profile (displayName + Firestore settings)
  const updateUserProfile = async ({ displayName, settings }) => {
    if (!user) return;
    try {
      if (displayName && displayName !== user.displayName) {
        await updateProfile(auth.currentUser, { displayName });
      }
      const updates = {};
      if (displayName) updates.displayName = displayName;
      if (settings) updates.settings = settings;
      await updateDoc(doc(db, 'users', user.uid), updates);
      setUser(prev => ({ ...prev, ...updates }));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Get remaining upload count for free users
  const getRemainingUploads = () => {
    if (!user) return 0;
    if (user.isPro) return Infinity;
    return Math.max(0, FREE_TIER.maxSheets - (user.sheetCount || 0));
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      subscription,
      signIn,
      signUp,
      signOut,
      signInWithGoogle,
      checkCanUpload,
      getRemainingUploads,
      updateUserProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default useAuth;
